import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import * as http from '../../common/http';
import { APIUrl } from '../../common/apiConfig';

interface EnrollmentTrendsData {
    daily_counts: Array<{
        date: string;
        count: number;
    }>;
    program_breakdown: Array<{
        program: string;
        count: number;
    }>;
    growth_percentage: number;
    period_days: number;
}

const EnrollmentTrends: React.FC<{ branchId?: number; days?: number }> = ({ 
    branchId, 
    days = 30 
}) => {
    const [data, setData] = useState<EnrollmentTrendsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let url = `${APIUrl}dashboard-coaching/enrollment-trends?days=${days}`;
            if (branchId) {
                url += `&branch_id=${branchId}`;
            }
            
            const response = await http.get(url);
            if (response && response.status === 'success') {
                setData(response.result);
                setLastRefresh(new Date());
            } else {
                setError('Failed to fetch enrollment trends');
            }
        } catch (err) {
            console.error('Error fetching enrollment trends:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [branchId, days]);

    const totalEnrollments = data?.daily_counts.reduce((sum, item) => sum + item.count, 0) || 0;

    if (loading && !data) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                        Enrollment Trends
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        {data && (
                            <Chip 
                                icon={data.growth_percentage >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                label={`${data.growth_percentage >= 0 ? '+' : ''}${data.growth_percentage}%`}
                                color={data.growth_percentage >= 0 ? 'success' : 'error'}
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                        )}
                        <Tooltip title="Refresh">
                            <IconButton size="small" onClick={fetchData} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Summary Stats */}
                <Box 
                    p={2} 
                    mb={2} 
                    sx={{ 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: 2,
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Enrollments (Last {data?.period_days} days)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="primary">
                        {totalEnrollments}
                    </Typography>
                </Box>

                {/* Simple Bar Chart Visualization */}
                {data?.daily_counts && data.daily_counts.length > 0 ? (
                    <Box mb={2}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Daily Enrollments
                        </Typography>
                        <Box 
                            display="flex" 
                            alignItems="flex-end" 
                            gap={0.5} 
                            height={100}
                            sx={{ overflowX: 'auto' }}
                        >
                            {data.daily_counts.map((item, index) => {
                                const maxCount = Math.max(...data.daily_counts.map(d => d.count), 1);
                                const heightPercentage = (item.count / maxCount) * 100;
                                
                                return (
                                    <Tooltip 
                                        key={index} 
                                        title={`${item.date}: ${item.count} enrollments`}
                                        arrow
                                    >
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: '8px',
                                                height: `${heightPercentage}%`,
                                                backgroundColor: item.count > 0 ? '#1976d2' : '#e0e0e0',
                                                borderRadius: '2px 2px 0 0',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s',
                                                '&:hover': {
                                                    backgroundColor: item.count > 0 ? '#1565c0' : '#bdbdbd'
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            Hover over bars for details
                        </Typography>
                    </Box>
                ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>No enrollment data available</Alert>
                )}

                {/* Program Breakdown */}
                {data?.program_breakdown && data.program_breakdown.length > 0 && (
                    <>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Program Breakdown
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {data.program_breakdown.map((item, index) => (
                                <Chip 
                                    key={index}
                                    label={`${item.program}: ${item.count}`}
                                    variant="outlined"
                                    size="small"
                                />
                            ))}
                        </Box>
                    </>
                )}

                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default EnrollmentTrends;
